/**
 * System registries — Connectors, Skills, Permissions catalog, Templates,
 * and per-role grants (Milestone 1.5b, spec §3.5 + §3.12 + §8).
 *
 * Mirrors:
 *   - chaos.connectors             (system-wide)
 *   - chaos.skills                 (system-wide)
 *   - chaos.permissions_catalog    (system-wide)
 *   - chaos.role_connector_grants  (per-role join, idempotent)
 *   - chaos.role_skill_grants      (per-role join, idempotent)
 *   - chaos.role_permissions       (per-role join, idempotent)
 *   - chaos.templates              (system-wide template library)
 *
 * Convention same as orgs.ts / org-structure.ts: camelCase wire shape,
 * ISO timestamps, uuid strings. snake_case `permission` PK is exposed as
 * `permission` (single-word — no transform needed).
 */
import { z } from 'zod';

// ─────────────────────────────────────────────────────────────────────
// Connectors (system registry)
// ─────────────────────────────────────────────────────────────────────

export const connectorCostClasses = ['free', 'metered', 'paid'] as const;
export const connectorCostClassSchema = z.enum(connectorCostClasses);
export type ConnectorCostClass = z.infer<typeof connectorCostClassSchema>;

export const connectorSchema = z.object({
  id: z.string().uuid(),
  slug: z.string().min(1).max(120),
  displayName: z.string().min(1).max(200),
  description: z.string().nullable(),
  category: z.string().nullable(),
  mcpConfig: z.unknown(),
  costClass: z.string().nullable(),
  enabled: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Connector = z.infer<typeof connectorSchema>;

export const createConnectorSchema = z.object({
  slug: z
    .string()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9][a-z0-9._-]*$/, 'lowercase alphanumeric + ._- only'),
  displayName: z.string().min(1).max(200),
  description: z.string().max(4000).nullable().optional(),
  category: z.string().max(120).nullable().optional(),
  mcpConfig: z.unknown(),
  costClass: z.string().max(40).nullable().optional(),
  enabled: z.boolean().optional(),
});
export type CreateConnectorInput = z.infer<typeof createConnectorSchema>;

export const updateConnectorSchema = z.object({
  displayName: z.string().min(1).max(200).optional(),
  description: z.string().max(4000).nullable().optional(),
  category: z.string().max(120).nullable().optional(),
  mcpConfig: z.unknown().optional(),
  costClass: z.string().max(40).nullable().optional(),
  enabled: z.boolean().optional(),
});
export type UpdateConnectorInput = z.infer<typeof updateConnectorSchema>;

// ─────────────────────────────────────────────────────────────────────
// Skills (system registry)
// ─────────────────────────────────────────────────────────────────────

export const skillSchema = z.object({
  id: z.string().uuid(),
  slug: z.string().min(1).max(120),
  displayName: z.string().min(1).max(200),
  description: z.string().nullable(),
  body: z.string(),
  category: z.string().nullable(),
  enabled: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Skill = z.infer<typeof skillSchema>;

export const createSkillSchema = z.object({
  slug: z
    .string()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9][a-z0-9._-]*$/, 'lowercase alphanumeric + ._- only'),
  displayName: z.string().min(1).max(200),
  description: z.string().max(4000).nullable().optional(),
  body: z.string().min(1).max(1_000_000),
  category: z.string().max(120).nullable().optional(),
  enabled: z.boolean().optional(),
});
export type CreateSkillInput = z.infer<typeof createSkillSchema>;

export const updateSkillSchema = z.object({
  displayName: z.string().min(1).max(200).optional(),
  description: z.string().max(4000).nullable().optional(),
  body: z.string().min(1).max(1_000_000).optional(),
  category: z.string().max(120).nullable().optional(),
  enabled: z.boolean().optional(),
});
export type UpdateSkillInput = z.infer<typeof updateSkillSchema>;

// ─────────────────────────────────────────────────────────────────────
// Permissions catalog
// ─────────────────────────────────────────────────────────────────────

export const permissionCatalogSchema = z.object({
  permission: z.string().min(1).max(200),
  displayName: z.string().min(1).max(200),
  category: z.string().nullable(),
  description: z.string().nullable(),
  defaultGrant: z.boolean(),
  isMutating: z.boolean(),
});
export type PermissionCatalog = z.infer<typeof permissionCatalogSchema>;

/** Runtime writers may never reintroduce the retired/placeholder vocabulary.
 * `card.comment` is migration-0361 input only; `*` and `instructions.edit` are
 * reserved sentinels/placeholders, not catalog actions. */
export const RESERVED_PERMISSION_ACTIONS: readonly string[] = Object.freeze([
  '*',
  'card.comment',
  'instructions.edit',
]);

export const createPermissionSchema = z.object({
  permission: z
    .string()
    .min(1)
    .max(200)
    .regex(/^[a-z][a-z0-9._:-]*$/, 'lowercase, allowed: a-z 0-9 . _ : -')
    .refine(
      (permission) => !RESERVED_PERMISSION_ACTIONS.includes(permission),
      'reserved permission action',
    ),
  displayName: z.string().min(1).max(200),
  category: z.string().max(120).nullable().optional(),
  description: z.string().max(4000).nullable().optional(),
  defaultGrant: z.boolean().optional(),
  isMutating: z.boolean(),
});
export type CreatePermissionInput = z.infer<typeof createPermissionSchema>;

export const updatePermissionSchema = z.object({
  displayName: z.string().min(1).max(200).optional(),
  category: z.string().max(120).nullable().optional(),
  description: z.string().max(4000).nullable().optional(),
  defaultGrant: z.boolean().optional(),
});
export type UpdatePermissionInput = z.infer<typeof updatePermissionSchema>;

// ─────────────────────────────────────────────────────────────────────
// Per-role grants
// ─────────────────────────────────────────────────────────────────────

export const roleConnectorGrantSchema = z.object({
  roleId: z.string().uuid(),
  connectorId: z.string().uuid(),
  grantedBy: z.string().uuid().nullable(),
  grantedAt: z.string().datetime(),
});
export type RoleConnectorGrant = z.infer<typeof roleConnectorGrantSchema>;

export const createRoleConnectorGrantSchema = z.object({
  connector_id: z.string().uuid(),
});
export type CreateRoleConnectorGrantInput = z.infer<typeof createRoleConnectorGrantSchema>;

/**
 * Reach of a skill grant (migration 0217). The founder's words were
 * «на всех досках роль может использовать этот навык или нет», and the
 * decision he made is that this is a property of the GRANT — the role↔skill
 * pair — not of the board and not of the skill.
 *
 * 'all' is the default and is what all 99 pre-0217 grants mean. 'selected'
 * with an EMPTY boardIds reaches NO board; it is never widened back to 'all'.
 */
export const roleSkillGrantBoardScopes = ['all', 'selected'] as const;
export const roleSkillGrantBoardScopeSchema = z.enum(roleSkillGrantBoardScopes);
export type RoleSkillGrantBoardScope = z.infer<typeof roleSkillGrantBoardScopeSchema>;

export const roleSkillGrantSchema = z.object({
  roleId: z.string().uuid(),
  skillId: z.string().uuid(),
  grantedBy: z.string().uuid().nullable(),
  grantedAt: z.string().datetime(),
  boardScope: roleSkillGrantBoardScopeSchema,
  /** Named boards. Always present; empty for boardScope 'all'. */
  boardIds: z.array(z.string().uuid()),
});
export type RoleSkillGrant = z.infer<typeof roleSkillGrantSchema>;

export const createRoleSkillGrantSchema = z.object({
  skill_id: z.string().uuid(),
});
export type CreateRoleSkillGrantInput = z.infer<typeof createRoleSkillGrantSchema>;

/**
 * Body of PUT …/skill-grants/:skillId/board-scope.
 *
 * The list is stated in FULL every time, and only alongside 'selected':
 *
 *   scope 'all'      → board_ids must be ABSENT. A request that says "every
 *                      board" while also naming boards is ambiguous about
 *                      which half the caller meant, and guessing at that is
 *                      how a UI bug turns into a silent widening.
 *   scope 'selected' → board_ids is REQUIRED and REPLACES whatever was there.
 *                      Omitting it would mean "narrow it, but I will not say
 *                      to what", which has no safe reading.
 *
 * An EMPTY list with 'selected' is accepted and means the grant reaches no
 * board at all. That is a real thing a founder may want — park a skill without
 * revoking it — and the settings screen says so in words rather than letting
 * it look like an ordinary restriction.
 */
export const setRoleSkillGrantBoardScopeSchema = z
  .object({
    scope: roleSkillGrantBoardScopeSchema,
    board_ids: z.array(z.string().uuid()).max(500).optional(),
  })
  .refine(
    (v) => (v.scope === 'selected' ? Array.isArray(v.board_ids) : v.board_ids === undefined),
    {
      message:
        "board_ids is required with scope 'selected' (it replaces the list) and must be omitted with scope 'all'",
      path: ['board_ids'],
    },
  );
export type SetRoleSkillGrantBoardScopeInput = z.infer<
  typeof setRoleSkillGrantBoardScopeSchema
>;

export const rolePermissionSchema = z.object({
  roleId: z.string().uuid(),
  permission: z.string(),
  grantedBy: z.string().uuid().nullable(),
  grantedAt: z.string().datetime(),
});
export type RolePermission = z.infer<typeof rolePermissionSchema>;

export const createRolePermissionSchema = z.object({
  permission: z.string().min(1).max(200),
});
export type CreateRolePermissionInput = z.infer<typeof createRolePermissionSchema>;

// ─────────────────────────────────────────────────────────────────────
// Templates (§3.12)
// ─────────────────────────────────────────────────────────────────────

export const templateCategories = [
  'constitution',
  'division',
  'department',
  'role',
  'project',
  'board',
  'column',
  'card',
  'sub_task',
] as const;
export const templateCategorySchema = z.enum(templateCategories);
export type TemplateCategory = z.infer<typeof templateCategorySchema>;

export const templateSchema = z.object({
  id: z.string().uuid(),
  category: templateCategorySchema,
  slug: z.string().min(1).max(120),
  displayName: z.string().min(1).max(200),
  description: z.string().nullable(),
  body: z.string(),
  metadata: z.unknown().nullable(),
  isOfficial: z.boolean(),
  createdBy: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Template = z.infer<typeof templateSchema>;

export const createTemplateSchema = z.object({
  category: templateCategorySchema,
  slug: z
    .string()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9][a-z0-9._-]*$/, 'lowercase alphanumeric + ._- only'),
  displayName: z.string().min(1).max(200),
  description: z.string().max(4000).nullable().optional(),
  body: z.string().min(1).max(1_000_000),
  metadata: z.unknown().nullable().optional(),
  isOfficial: z.boolean().optional(),
});
export type CreateTemplateInput = z.infer<typeof createTemplateSchema>;

export const updateTemplateSchema = z.object({
  category: templateCategorySchema.optional(),
  displayName: z.string().min(1).max(200).optional(),
  description: z.string().max(4000).nullable().optional(),
  body: z.string().min(1).max(1_000_000).optional(),
  metadata: z.unknown().nullable().optional(),
  isOfficial: z.boolean().optional(),
});
export type UpdateTemplateInput = z.infer<typeof updateTemplateSchema>;

// Apply-template request shape. Discriminated by target_kind so the
// resolver knows which DB table to update.
export const templateTargetKinds = [
  'org_constitution',
  'division',
  'department',
  'role',
  'project',
  'board',
  'column',
  'card',
  'sub_task',
] as const;
export const templateTargetKindSchema = z.enum(templateTargetKinds);
export type TemplateTargetKind = z.infer<typeof templateTargetKindSchema>;

export const applyTemplateSchema = z.object({
  target_kind: templateTargetKindSchema,
  target_id: z.string().uuid(),
  placeholders: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
  change_note: z.string().max(1000).nullable().optional(),
});
export type ApplyTemplateInput = z.infer<typeof applyTemplateSchema>;

export interface ApplyTemplateResult {
  applied: true;
  target_kind: TemplateTargetKind;
  target_id: string;
  new_version?: number; // populated when target_kind === 'org_constitution'
}
